import { useState, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { Branch } from "@/types/branch";

interface UseTipSelectionProps {
  branch: Branch | undefined;
  subtotal: number;
}

export function useTipSelection({ branch, subtotal }: UseTipSelectionProps) {
  const [selectedTip, setSelectedTip] = useState<number | null>(
    branch?.tip_1 || 0,
  );
  const [customTip, setCustomTip] = useState<number | null>(null);

  const tipOptions = useMemo(
    () => [
      { value: branch?.tip_1 || 0, label: `${branch?.tip_1}%` },
      { value: branch?.tip_2 || 0, label: `${branch?.tip_2}%` },
      { value: branch?.tip_3 || 0, label: `${branch?.tip_3}%` },
    ],
    [branch?.tip_1, branch?.tip_2, branch?.tip_3],
  );

  const tipAmount = useMemo(() => {
    return selectedTip !== null
      ? subtotal * (selectedTip / 100)
      : customTip || 0;
  }, [selectedTip, subtotal, customTip]);

  const handleTipSelection = useCallback((percentage: number) => {
    setSelectedTip(percentage);
    setCustomTip(null);
  }, []);

  const handleCustomTipInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(event.target.value);
      if (isNaN(value)) {
        setCustomTip(null);
      } else if (value < 0) {
        setCustomTip(null);
        toast.error("Invalid custom tip. Tip cannot be negative.");
      } else {
        setCustomTip(value);
      }
      setSelectedTip(null);
    },
    [],
  );

  const initializeTip = useCallback((defaultTip: number) => {
    setSelectedTip(defaultTip);
  }, []);

  return {
    selectedTip,
    customTip,
    tipOptions,
    tipAmount,
    handleTipSelection,
    handleCustomTipInput,
    initializeTip,
  };
}
